import React, { useState, useEffect } from "react";
import axios from "axios";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {
  baseUrl,
  userLocation,
  ipAddress,
  userName
} from "../Univ.js";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from "recharts";

function PharmProfit() {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [rows, setRows] = useState([]);
  const [combinedData, setcombinedData] = useState([]);
  const [viewPharmData, setViewPharmData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ---------------- FETCH SALES ----------------
  async function fetchData() {
    setIsLoading(true);

    const start = startDate ? startDate.toISOString().substring(0, 10) : "";
    const end = endDate ? endDate.toISOString().substring(0, 10) : "";

    try {
      const res = await axios.get(
        `${baseUrl}all-pharm-sale/?start_date=${start}&end_date=${end}&status=1`
      );

      setcombinedData(res.data);
      setIsLoading(false);
    } catch (err) {
      console.log(err);
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  // ---------------- FETCH PHARMACY DATA ----------------
  async function fetchPharmData() {
    try {
      const productIds = combinedData.reduce((ids, row) => {
        const products = (row.product || "")
          .toString()
          .split(",")
          .map(i => i.trim())
          .filter(Boolean);

        return [...ids, ...products];
      }, []);

      const uniqueIds = [...new Set(productIds)];

      const indentResponses = await Promise.all(
        uniqueIds.map(id =>
          axios.get(`${baseUrl}update-indent-use/${id}/`)
        )
      );

      const saleToPharmaMap = {};

      indentResponses.forEach(res => {
        saleToPharmaMap[res.data.id] = res.data.product;
      });

      const pharmaIds = [...new Set(Object.values(saleToPharmaMap))];

      const pharmResponses = await Promise.all(
        pharmaIds.map(id =>
          axios.get(`${baseUrl}update-pharm-qty/${id}/`)
        )
      );

      const pharmaMap = {};
      pharmResponses.forEach(res => {
        pharmaMap[res.data.id] = res.data;
      });

      const updated = combinedData.map(row => {
        const ids = (row.product || "").split(",");
        const qtys = (row.qty || "").split(",");

        return {
          ...row,
          enriched: ids.map((saleId, i) => {
            const pharmaId = saleToPharmaMap[saleId];
            return {
              qty: parseFloat(qtys[i] || 0),
              data: pharmaMap[pharmaId] || null
            };
          })
        };
      });

      setRows(updated);
      setViewPharmData(pharmaMap);
    } catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    if (combinedData.length > 0) {
      fetchPharmData();
    }
  }, [combinedData]);

  // ---------------- CALCULATIONS ----------------
  const totalMrp = rows.reduce((t, r) => {
    let s = 0;
    (r.enriched || []).forEach(i => {
      if (i.data) s += parseFloat(i.data.mrp || 0) * i.qty;
    });
    return t + s;
  }, 0);

  const totalPurchase = rows.reduce((t, r) => {
    let s = 0;
    (r.enriched || []).forEach(i => {
      if (i.data) s += parseFloat(i.data.pprice || 0) * i.qty;
    });
    return t + s;
  }, 0);

  const totalPaid = rows.reduce(
    (t, r) => t + parseFloat(r.paid || 0),
    0
  );

  const profit = totalMrp - totalPurchase;
  const profitPercent =
    totalPurchase > 0 ? ((profit / totalPurchase) * 100).toFixed(2) : 0;

  // ---------------- CHART DATA ----------------
  const chartData = [
    { name: "MRP", value: totalMrp },
    { name: "Purchase", value: totalPurchase },
    { name: "Paid", value: totalPaid },
    { name: "Profit", value: profit }
  ];

  const columns = [
    { field: "mrp", headerName: "MRP", width: 120 },
    { field: "purchase", headerName: "Purchase", width: 120 },
    { field: "paid", headerName: "Paid", width: 120 }
  ];

  const summaryRow = {
    id: "summary",
    mrp: totalMrp.toFixed(2),
    purchase: totalPurchase.toFixed(2),
    paid: totalPaid.toFixed(2)
  };

  return (
    <section className="container mt-4">

      <h3 className="text-center bg-info text-white p-2">
        Pharmacy Profit Dashboard
      </h3>

      {/* ---------------- FILTER ---------------- */}
      <div className="row mb-3">
        <div className="col-md-4">
          <DatePicker
            selected={startDate}
            onChange={setStartDate}
            placeholderText="From Date"
          />
        </div>

        <div className="col-md-4">
          <DatePicker
            selected={endDate}
            onChange={setEndDate}
            placeholderText="To Date"
          />
        </div>
      </div>

      {/* ---------------- KPI CARDS ---------------- */}
      <div className="row text-center mb-3">

        <div className="col-md-3">
          <div className="card p-2 bg-light">
            <h6>Total MRP</h6>
            <h4>₹ {totalMrp.toFixed(2)}</h4>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card p-2 bg-light">
            <h6>Purchase</h6>
            <h4>₹ {totalPurchase.toFixed(2)}</h4>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card p-2 bg-light">
            <h6>Profit</h6>
            <h4>₹ {profit.toFixed(2)}</h4>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card p-2 bg-light">
            <h6>Profit %</h6>
            <h4>{profitPercent}%</h4>
          </div>
        </div>
      </div>

      {/* ---------------- CHART ---------------- */}
      <div style={{ width: "100%", height: 300 }}>
        <ResponsiveContainer>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="value" fill="#0dcaf0" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ---------------- TABLE ---------------- */}
      <DataGrid
                  sx={{
              boxShadow: 4,
              border: 4,
              borderColor: 'primary.light',
              '& .MuiDataGrid-cell:hover': {
                color: 'primary.main',
              },
            }}

        rows={[summaryRow]}
        columns={columns}
        autoHeight
        slots={{ toolbar: GridToolbar }}
      />
    </section>
  );
}

export default PharmProfit;